import { add, isBefore, format } from "date-fns";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Text, Button, Tooltip } from "src/components";
import { Item, Milestone } from "src/models";
import { ItemStores } from "src/stores";
import { DataType, IItem } from "src/types";
import { getDateDiff, startOf } from "src/util/util";
import { Card, CardGrid } from "src/views";
import styled from "styled-components";
import { StickyTableCell } from "../components/stickyTableCell";
import { TableDataCounter } from "../components/tableDataCounter";
import Box from "react-raster";

export interface IMilestoneRowProps {
  milestones: Milestone[],
  view: "days" | "weeks" | "months",
  start: Date,
  end: Date,
  itemStores?: ItemStores;
}

export const MilestoneRow = inject("itemStores")(observer((props: IMilestoneRowProps) => {
  const { milestones, view, start, end, itemStores } = props;
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = React.useState(false);

  const cells = [];
  const _milestones: Milestone[] = (milestones || []).slice();
  _milestones.sort((m1: Milestone, m2: Milestone) => m1.date.getTime() - m2.date.getTime());

  for (let curDate = startOf(start, view); isBefore(curDate, end); curDate = add(curDate, { [view]: 1 })) {
    const m: Milestone[] = [];

    while (_milestones.length && getDateDiff(_milestones[0].date, curDate, view) < 1)
      m.push(_milestones.shift());

    cells.push(
      <td key={curDate.toString() + "milestones"} style={{ height: '1.5rem' }}>
        {m.length > 0 && (
          <Tooltip
            placement = "left"
            content   = {m.map( i => (<p style={{marginBottom: '0.5rem'}}>{`${format(i.date, "yyyy-MM-dd")} - ${i.title}`}</p>))}
          >
          <TableDataCounter 
            variant = "milestone"
            onClick = {() => {
              itemStores.deliverable.setFilterValue("milestones", m.map(_m => ({key: ""+_m.id, label: _m.title, value: _m})));
              itemStores.deliverable.updateQuery();
              itemStores.deliverable.advSearch = true;
            }}
          >
            <span>{m.length}</span>
          </TableDataCounter>
          </Tooltip>
        )}
      </td>
    );
  }

  return (
    <>
      <StickyTableCell style={{ cursor: "pointer" }} onClick={() => setShowModal(true)}>
        <Text as="h2" size="default" weight="bold" color="grayDark">
          {t("milestone_plural")}
        </Text>
      </StickyTableCell>
      <MilestoneModal show={showModal} onClose={() => setShowModal(false)}>
        <Box padding='2rem'>
          <Box display='flex' flexDirection='row' marginBottom='1rem' width='100%'>
            <Box>
              <Button
                variant = "primary"
                size    = "default"
                onClick = {() => itemStores[DataType.Milestone].setActive(itemStores[DataType.Milestone].createNew())} >Skapa milstople</Button>
            </Box>
          </Box>
          <StyledCardGrid>
            {milestones.map((i: Item<IItem>) => (
              <Card
                key     = {i.id}
                data    = {i}
                onClick = {() => itemStores.milestone.setActive(i)}
                type    = {DataType.Milestone}
              />
            ))}
          </StyledCardGrid>
        </Box>
      </MilestoneModal>
      {cells}
    </>
  );
}))

const MilestoneModal = styled(Modal)`
  overflow-y: hidden;
  max-height: 50rem;
  max-width:  62rem;
`

const StyledCardGrid = styled(CardGrid)`
  padding:         0rem 0rem;
  overflow-y:      visible;
  justify-content: flex-start;
`;


